import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { WarningCircle, Plus, X, CheckCircle } from "@phosphor-icons/react";

const PRIORITY_COLORS = {
  low: "bg-gray-100 text-gray-700 border-gray-300",
  medium: "bg-blue-100 text-blue-800 border-blue-300",
  high: "bg-orange-100 text-orange-800 border-orange-400",
  critical: "bg-red-100 text-red-800 border-red-500",
};
const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-800 border-amber-300",
  in_progress: "bg-blue-100 text-blue-800 border-blue-300",
  resolved: "bg-green-100 text-green-800 border-green-400",
  closed: "bg-gray-100 text-gray-700 border-gray-300",
};

export default function Issues() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(can("view_all_issues") ? "all" : "mine");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (tab === "mine") p.set("mine", "true");
      if (status) p.set("status", status);
      if (priority) p.set("priority", priority);
      if (q) p.set("q", q);
      const r = await api.get(`/issues?${p}`);
      setItems(r.data.issues || []);
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, status, priority, q]);

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="eyebrow mb-2 flex items-center gap-2"><WarningCircle size={12} weight="bold" /> 04 / Issues</div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-black tracking-tight">Issues & Concerns</h1>
          <button onClick={() => setDlg({ mode: "create" })} className="btn-primary flex items-center gap-2" data-testid="new-issue-btn">
            <Plus size={14} weight="bold" /> Raise an issue
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Raise issues, track their resolution, and know who owns them. Use a template for a guided form.
        </p>
      </div>

      {msg.text && (
        <div className={`mb-4 text-xs px-3 py-2 border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {can("view_all_issues") && (
          <div className="inline-flex border border-border">
            <button onClick={() => setTab("all")} className={`px-3 py-1.5 text-xs font-semibold ${tab === "all" ? "bg-[#002FA7] text-white" : "bg-white"}`}>All</button>
            <button onClick={() => setTab("mine")} className={`px-3 py-1.5 text-xs font-semibold border-l border-border ${tab === "mine" ? "bg-[#002FA7] text-white" : "bg-white"}`}>Mine</button>
          </div>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border px-3 py-1.5 text-sm bg-white">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="border border-border px-3 py-1.5 text-sm bg-white">
          <option value="">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / description" className="border border-border px-3 py-1.5 text-sm bg-white min-w-[260px]" />
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Title</th>
              <th className="text-left px-3 py-2 font-semibold">Category</th>
              <th className="text-left px-3 py-2 font-semibold">Priority</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Raised by</th>
              <th className="text-left px-3 py-2 font-semibold">Assigned to</th>
              <th className="text-left px-3 py-2 font-semibold">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No issues found.</td></tr>
              : items.map(i => (
                <tr key={i.id} onClick={() => nav(`/issues/${i.id}`)} className="border-b border-border hover:bg-muted/40 cursor-pointer" data-testid={`issue-row-${i.id}`}>
                  <td className="px-3 py-2 font-semibold">{i.title}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{i.category || "—"}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${PRIORITY_COLORS[i.priority] || ""}`}>{i.priority}</span></td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${STATUS_COLORS[i.status] || ""}`}>{i.status}</span></td>
                  <td className="px-3 py-2 text-xs">{i.raised_by_user?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{i.assigned_to_user?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {dlg && <NewIssueDialog onClose={() => setDlg(null)} onSaved={async (id) => { setDlg(null); await load(); flash("ok", "Issue raised."); if (id) nav(`/issues/${id}`); }} can={can} />}
    </div>
  );
}

function NewIssueDialog({ onClose, onSaved, can }) {
  const [tpls, setTpls] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", template_id: "", category: "", priority: "medium", assigned_to: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [tr, ur] = await Promise.all([
        api.get("/issue-templates"),
        can("assign_issues") ? api.get("/users") : Promise.resolve({ data: { users: [] } }),
      ]);
      setTpls(tr.data.templates || []);
      setUsers(ur.data.users || ur.data || []);
    } catch { /* ignore */ }
  })(); }, [can]);

  const pickTpl = (tid) => {
    const t = tpls.find(x => x.id === tid);
    if (!t) { setForm({ ...form, template_id: "" }); return; }
    setForm({
      ...form,
      template_id: t.id,
      category: t.category || form.category,
      priority: t.default_priority || form.priority,
      description: t.description_template || form.description,
    });
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const r = await api.post("/issues", {
        title: form.title.trim(),
        description: form.description,
        template_id: form.template_id,
        category: form.category.trim(),
        priority: form.priority,
        assigned_to: form.assigned_to || "",
      });
      await onSaved(r.data.id);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">Raise an issue</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Template</span>
            <select className="input" value={form.template_id} onChange={(e) => pickTpl(e.target.value)} data-testid="issue-template">
              <option value="">— None —</option>
              {tpls.map(t => <option key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Title*</span>
            <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="issue-title" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Category</span>
              <input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </label>
            <label className="block">
              <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Priority</span>
              <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="issue-priority">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </label>
          </div>
          {can("assign_issues") && (
            <label className="block">
              <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Assign to</span>
              <select className="input" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Description</span>
            <textarea className="input min-h-[180px] font-mono text-xs" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="issue-description" />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={saving || !form.title.trim()} className="btn-primary flex items-center gap-2" data-testid="save-issue">
            <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Raise issue"}
          </button>
        </div>
      </div>
    </div>
  );
}
